import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useNavigation } from './hooks/useNavigation';
import { SubNav, type SubNavProps } from '@/components/custom/SubNav';

// Import sub-components
import { NavigationLogo } from './NavigationLogo';
import { NavigationSearch, type NavigationSearchProps } from './NavigationSearch';
import { NavigationAccount, type NavigationAccountProps } from './NavigationAccount';
import { NavigationMenu, type NavItem } from './NavigationMenu';
import { NavigationMobile } from './NavigationMobile';

export type { NavItem, NavigationSearchProps, NavigationAccountProps };

export interface NavigationProps extends React.HTMLAttributes<HTMLElement> {
  logo?: React.ReactNode;
  items: NavItem[];
  activeKey?: string;
  onNavigate?: (key: string) => void;
  endContent?: React.ReactNode;
  search?: NavigationSearchProps;
  account?: NavigationAccountProps;
  subNav?: SubNavProps;
  /** Custom link component for routing (e.g., React Router Link) */
  linkComponent?: React.ElementType;
}

export const Navigation = React.forwardRef<HTMLElement, NavigationProps>(
  (
    { logo, items, activeKey, onNavigate, endContent, search, account, subNav, linkComponent, className, ...props },
    ref
  ) => {
    const {
      isSearchOpen,
      toggleSearch,
      setHoveredItem,
      shouldShowSubNav,
      resolvedVariant,
      resolvedSide,
      resolvedIsOpen,
      renderedActiveTab,
      handleNavItemClick,
      handleSubNavClose,
    } = useNavigation({ activeKey, onNavigate, subNav });
  
    // handlers are provided by useNavigation
    

    return (
      <div className="w-full relative">
        <nav
          ref={ref}
          role="navigation"
          aria-label="Main"
          className={cn('w-full bg-gradient-to-b from-sky-600 to-sky-700 text-gray-50', className)}
          {...props}
        >
        <div className="mx-auto flex items-center justify-between gap-3 px-4 md:px-6 lg:px-8 py-3">
          {/* Left: Logo */}
          <NavigationLogo logo={logo} />

          {/* Center: Search or Primary menu */}
          {search && isSearchOpen ? (
            <NavigationSearch
              {...search}
              isOpen={isSearchOpen}
              onToggle={toggleSearch}
            />
          ) : (
            <NavigationMenu
              items={items}
              activeKey={activeKey}
              onItemClick={handleNavItemClick}
              onItemHover={setHoveredItem}
              linkComponent={linkComponent}
            />
          )}

          {/* Right: Mobile menu + Quick actions */}
          <div className="flex items-center gap-2">
            {/* Mobile: Hamburger menu (only when items provided) */}
            {items && items.length > 0 && (
              <NavigationMobile
                items={items}
                activeKey={activeKey}
                onItemClick={handleNavItemClick}
                linkComponent={linkComponent}
              />
            )}

            {/* Desktop: Quick actions */}
            <div className="hidden md:flex items-center gap-2">
              {endContent ?? (
                account ? (
                  <>
                    {search && !isSearchOpen && (
                      <NavigationSearch
                        {...search}
                        isOpen={false}
                        onToggle={toggleSearch}
                        className=""
                      />
                    )}
                    <NavigationAccount {...account} />
                  </>
                ) : (
                  <>
                    {search && !isSearchOpen && (
                      <NavigationSearch
                        {...search}
                        isOpen={false}
                        onToggle={toggleSearch}
                      />
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "rounded-full px-3",
                        "md:bg-sky-100/90 md:text-sky-900 md:border-sky-200/50",
                        "lg:bg-white lg:text-slate-800 lg:border-gray-200 lg:ring-1 lg:ring-gray-200"
                      )}
                      aria-label="Account"
                    >
                      <span className="font-medium">AG</span>
                    </Button>
                  </>
                )
              )}
            </div>
          </div>
        </div>
        </nav>
        {subNav && shouldShowSubNav && (
          <div
            className={cn(
              'absolute top-full left-0 right-0 z-50 flex justify-center',
              resolvedVariant === 'card'
                ? 'px-4 md:px-6 lg:px-8 py-4 transition-all duration-200'
                : undefined
            )}
          >
            <div className="w-full max-w-5xl">
              <SubNav
                {...subNav}
                variant={resolvedVariant}
                side={resolvedSide}
                navigationData={subNav.navigationData}
                activeTab={renderedActiveTab}
                isOpen={resolvedIsOpen}
                linkComponent={linkComponent}
                onClose={() => {
                  handleSubNavClose();
                  subNav.onClose?.();
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);

Navigation.displayName = 'Navigation';

export default Navigation;
